import type { SmartSRPErrorCode } from '@/src/utils/srp/aiRecommendation/locale'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../rootReducer'

type JourneyLeg = 0 | 1

interface LegAIState {
    loading: boolean
    errorCode: SmartSRPErrorCode | null
    textInput: string
    lastSearchedInput: string | null
}

const initialLegState: LegAIState = {
    loading: false,
    errorCode: null,
    textInput: '',
    lastSearchedInput: null
}

interface AIRecommendationState {
    legs: [LegAIState, LegAIState]
}

const initialState: AIRecommendationState = {
    legs: [{ ...initialLegState }, { ...initialLegState }]
}

const aiRecommendationSlice = createSlice({
    name: 'aiRecommendation',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<{ leg: JourneyLeg; value: boolean }>) => {
            const legState = state.legs[action.payload.leg]
            legState.loading = action.payload.value
            if (action.payload.value) legState.errorCode = null
        },
        setErrorCode: (state, action: PayloadAction<{ leg: JourneyLeg; value: SmartSRPErrorCode | null }>) => {
            state.legs[action.payload.leg].errorCode = action.payload.value
        },
        setTextInput: (state, action: PayloadAction<{ leg: JourneyLeg; value: string }>) => {
            state.legs[action.payload.leg].textInput = action.payload.value
        },
        setLastSearchedInput: (state, action: PayloadAction<{ leg: JourneyLeg; value: string }>) => {
            state.legs[action.payload.leg].lastSearchedInput = action.payload.value
        },
        resetAIState: (state, action: PayloadAction<JourneyLeg>) => {
            state.legs[action.payload] = { ...initialLegState }
        },
        resetAllAIState: state => {
            state.legs = [{ ...initialLegState }, { ...initialLegState }]
        }
    }
})

export const { setLoading, setErrorCode, setTextInput, setLastSearchedInput, resetAIState, resetAllAIState } = aiRecommendationSlice.actions

const selectLeg = (state: RootState, leg: JourneyLeg): LegAIState =>
    state.aiRecommendation?.legs?.[leg] ?? initialLegState

export const selectAILoading = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).loading
export const selectAIErrorCode = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).errorCode
export const selectAITextInput = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).textInput
export const selectAILastSearchedInput = (state: RootState, leg: JourneyLeg) => selectLeg(state, leg).lastSearchedInput
export const selectHasActiveSmartPreferences = (state: RootState, leg: JourneyLeg) => !!selectLeg(state, leg).lastSearchedInput

export type { JourneyLeg }
export default aiRecommendationSlice.reducer
